import React, { useEffect, useState } from "react";
import {
    Avatar,
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    DialogActions,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    Button
} from "@mui/material";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";

import SecurityIcon from "@mui/icons-material/Security";
import Switch from "@mui/material/Switch";

import { ref, onValue, update } from "firebase/database";
import { rtdb } from "../../services/firebase";

export default function Users() {
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [pendingRoleChange, setPendingRoleChange] = useState(null);

    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);

    const [roleDialogOpen, setRoleDialogOpen] = useState(false);
    const [roleUser, setRoleUser] = useState(null);

    const [search, setSearch] = useState("");


    useEffect(() => {
        const usersRef = ref(rtdb, "users/client");

        return onValue(usersRef, snapshot => {
            if (!snapshot.exists()) {
                setUsers([]);
                return;
            }

            const data = snapshot.val();

            const list = Object.entries(data).map(([id, user]) => ({
                id,
                ...user,
                role: user.role || "client"
            }));

            setUsers(list);
        });
    }, []);

    function StatusChip({ status }) {
        const isActive = status === "active";

        return (
            <Box
                sx={{
                    display: "inline-block",
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 2,
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#fff",
                    backgroundColor: isActive ? "#2e7d32" : "#ef6c00"
                }}
            >
                {isActive ? "Ativo" : "Inativo"}
            </Box>
        );
    }

    function RoleLabel({ role }) {
        return role === "admin" ? "Admin" : "Cliente";
    }

    const filteredUsers = users.filter(user => {
        const term = search.toLowerCase();

        return (
            user.id?.toLowerCase().includes(term) ||
            user.firstName?.toLowerCase().includes(term) ||
            user.email?.toLowerCase().includes(term)
        );
    });

    return (
        <Paper sx={{ p: 4, borderRadius: 3, ml: 32 }}>
            <Box
                mb={3}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
            >
                <Typography variant="h4">
                    Usuários
                </Typography>

                <TextField
                    size="small"
                    placeholder="Buscar por ID, nome ou email"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ width: 320 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        )
                    }}
                />
            </Box>

            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>Foto</TableCell>
                        <TableCell>ID</TableCell>
                        <TableCell>Nome</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Tipo</TableCell>
                        <TableCell align="center">Ações</TableCell>
                    </TableRow>
                </TableHead>

                <TableBody>
                    {filteredUsers.map(user => (
                        <TableRow key={user.id}>
                            <TableCell>
                                <Avatar
                                    src={user.profileImage || ""}
                                    sx={{ bgcolor: !user.profileImage ? "grey.300" : undefined }}
                                >
                                    {!user.profileImage && <PersonOutlineIcon />}
                                </Avatar>
                            </TableCell>

                            <TableCell>{user.id}</TableCell>
                            <TableCell>{user.firstName} {user.lastName}</TableCell>
                            <TableCell>{user.email}</TableCell>
                            <TableCell>
                                <StatusChip status={user.status} />
                            </TableCell>
                            <TableCell>
                                <RoleLabel role={user.role} />
                            </TableCell>

                            <TableCell align="center">
                                <IconButton onClick={() => setSelectedUser(user)}>
                                    <InfoOutlinedIcon />
                                </IconButton>
                                <IconButton
                                    onClick={() => {
                                        setRoleUser(user);
                                        setRoleDialogOpen(true);
                                    }}
                                >
                                    <SecurityIcon />
                                </IconButton>
                            </TableCell>
                        </TableRow>
                    ))}

                    {users.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={7}>
                                <Typography align="center" color="text.secondary">
                                    Nenhum usuário cadastrado.
                                </Typography>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>

            {/* MODAL DE DETALHES */}
            <Dialog
                open={Boolean(selectedUser)}
                onClose={() => setSelectedUser(null)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Detalhes do Cliente</DialogTitle>

                <DialogContent dividers>
                    {selectedUser && (
                        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                            <Info label="ID" value={selectedUser.id} />
                            <Info label="Nome" value={selectedUser.name} />
                            <Info label="Email" value={selectedUser.email} />
                            <Info label="Telefone" value={selectedUser.address?.phone} />
                            <Info
                                label="Status"
                                value={<StatusChip status={selectedUser.status} />}
                            />
                            <Info label="Role" value={<RoleLabel role={selectedUser.role} />} />

                            <Info label="Documento" value={`${selectedUser.document?.type} - ${selectedUser.document?.value}`} />

                            <Info
                                label="Endereço"
                                value={`${selectedUser.address?.street}, ${selectedUser.address?.number} - ${selectedUser.address?.city}/${selectedUser.address?.state}`}
                            />

                            <Info label="CEP" value={selectedUser.address?.zipCode} />
                            <Info label="Referência" value={selectedUser.address?.referencePoint} />

                            <Info label="Marketing" value={selectedUser.preferences?.marketing ? "Sim" : "Não"} />
                            <Info label="WhatsApp" value={selectedUser.preferences?.whatsapp ? "Sim" : "Não"} />
                            <Info label="Atualizações de Pedido" value={selectedUser.preferences?.orderUpdates ? "Sim" : "Não"} />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button
                        onClick={() => setSelectedUser(null)}
                        variant="outlined"
                    >
                        Fechar
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={roleDialogOpen}
                onClose={() => setRoleDialogOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Permissão do Usuário</DialogTitle>

                <DialogContent dividers>
                    {roleUser && (
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Typography>
                                Tornar administrador
                            </Typography>

                            <Switch
                                checked={roleUser?.role === "admin"}
                                onChange={(e) => {
                                    const newRole = e.target.checked ? "admin" : "client";

                                    setPendingRoleChange({
                                        user: roleUser,
                                        newRole
                                    });

                                    setConfirmDialogOpen(true);
                                }}
                            />
                        </Box>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setRoleDialogOpen(false)}>
                        Fechar
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={confirmDialogOpen}
                onClose={() => setConfirmDialogOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>
                    Confirmar alteração de permissão
                </DialogTitle>

                <DialogContent dividers>
                    {pendingRoleChange && (
                        <Typography>
                            {pendingRoleChange.newRole === "admin"
                                ? "Tem certeza que você deseja tornar este usuário administrador?"
                                : "Tem certeza que você deseja remover o acesso administrativo deste usuário?"}
                        </Typography>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button
                        onClick={() => {
                            setConfirmDialogOpen(false);
                            setPendingRoleChange(null);
                        }}
                        variant="outlined"
                    >
                        Cancelar
                    </Button>

                    <Button
                        variant="contained"
                        color="warning"
                        onClick={async () => {
                            const { user, newRole } = pendingRoleChange;

                            await update(
                                ref(rtdb, `users/client/${user.id}`),
                                { role: newRole }
                            );

                            setConfirmDialogOpen(false);
                            setRoleDialogOpen(false);
                            setPendingRoleChange(null);
                            setRoleUser(null);
                        }}
                    >
                        Confirmar
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
}

function Info({ label, value }) {
    return (
        <Box>
            <Typography variant="caption" sx={{
                color: "var(--azul)",
                fontWeight: 500,
                letterSpacing: "0.04em"
            }}>
                {label}
            </Typography>
            <Typography>{value || "-"}</Typography>
        </Box>
    );
}